import { type MutationOptions, useMutation } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { downloadFile, extractFilename } from 'shared/utils/view';
import { z } from 'zod/v4';

const PreviewTemplateSchema = z.object({
  id: z.string(),
  download: z.boolean(),
  data: z.object({
    body: z.string(),
    subject: z.string().nullable(),
    locale: z.string(),
    groupedType: z.string().nullable().optional(),
    templateType: z.enum(['email', 'post']),
    stepType: z.string(),
    includePdf: z.boolean(),
    includeUbl: z.boolean(),
    fromName: z.string().nullable(),
    fromEmail: z.string().nullable(),
    replyToName: z.string().nullable(),
    replyToEmail: z.string().nullable(),
  }),
});

export type PreviewTemplateVariables = z.infer<typeof PreviewTemplateSchema>;

async function previewTemplateFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: PreviewTemplateVariables,
): Promise<ApiResponse> {
  const { id, download, data } = PreviewTemplateSchema.parse(variables);
  const instance = await axiosInstance;

  if (download) {
    const response = await instance.post(`/templates/${id}/template_preview`, data, {
      responseType: 'blob',
    });
    if (response.status === 200) {
      downloadFile(response.data, extractFilename(response.headers) ?? 'template');
    }
    return response;
  }

  return instance.post(`/templates/${id}/template_preview`, data);
}

export function usePreviewTemplate(
  options: MutationOptions<ApiResponse, ApiError, PreviewTemplateVariables> = {},
) {
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, PreviewTemplateVariables>({
    mutationFn: variables => previewTemplateFn(axiosInstance, variables),
    ...options,
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    PreviewTemplateVariables,
    'previewTemplate'
  >('previewTemplate', mutation);
}
